/**
 * Converte um Date local para o início do dia em UTC ISO.
 * Usado em filtros de range em endpoints — envia o dia inteiro independente do fuso.
 */
export function toIsoStartOfDay(date?: Date): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

export function toIsoEndOfDay(date?: Date): string | undefined {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T23:59:59.999Z`;
}

/** Formata uma string ISO (ou YYYY-MM-DD) como "dd/mm/yyyy" pt-BR. Retorna "—" se inválido. */
export function formatDateShort(iso?: string | null): string {
  if (!iso) return "—";
  const datePart = iso.includes("T") ? iso.split("T")[0] : iso;
  const [y, m, d] = datePart.split("-");
  if (!y || !m || !d) return "—";
  return `${d}/${m}/${y}`;
}

const MONTH_LABELS_PT = [
  "",
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const MONTH_LABELS_EN = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTH_LABELS_ES = [
  "",
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

function localeShortMonths(locale: string): string[] {
  if (locale.startsWith("en")) return MONTH_LABELS_EN;
  if (locale.startsWith("es")) return MONTH_LABELS_ES;
  return MONTH_LABELS_PT;
}

/**
 * Deriva uma chave/label/sort a partir do mês/ano de uma data ISO.
 * Loga warning em dev para datas malformadas.
 */
export function monthKey(iso: string, locale = "pt-BR"): {
  key: string;
  label: string;
  sort: number;
} {
  const datePart = iso?.includes("T") ? iso.split("T")[0] : iso;
  const [yStr, mStr] = (datePart ?? "").split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) {
    if (import.meta.env.DEV) {
      console.warn("[monthKey] data inválida:", iso);
    }
    return { key: iso ?? "invalid", label: iso ?? "—", sort: 0 };
  }
  const months = localeShortMonths(locale);
  return {
    key: `${y}-${String(m).padStart(2, "0")}`,
    label: `${months[m]}/${y}`,
    sort: y * 100 + m,
  };
}
